import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { User, xhr as sdkXhr } from "coolremote-sdk";
import { Button, ChevronLeft, IconButton, TextField, Typography } from "@coolremote/shared";
import HVACImg from "../../images/coolmaster.png";
import { DiscoveryFlowState, hasDiscoveryResult } from "./discoveryFlowState";
import { NewUserFlowState } from "./NewUserFlow/newUserFlowState";
import { clearPublicRegistrationTokenFlag } from "./publicRegistrationSession";
import { useStoreActions } from "../../models/RootStore";
import parentStyles from "./DeviceRegistrationPublic.module.css";

interface CongratulationsStepProps {
  /** Redirect target when the discovery result hasn't been resolved yet. */
  previousStepPath: string;
}

/**
 * The success screen — step 5 of both branches. Built for the new-user
 * branch in CM-618; shared verbatim with the existing-user branch (CM-619)
 * via `previousStepPath`, the only thing that differs between the branches.
 */
const CongratulationsStep: React.FC<CongratulationsStepProps> = ({ previousStepPath }) => {
  const history = useHistory<DiscoveryFlowState | undefined>();
  const deviceState = history.location.state;
  const setLoggedIn = useStoreActions((actions) => actions.setLoggedIn);
  const setSelections = useStoreActions((actions) => actions.selections.setSelections);
  const setMobileSelections = useStoreActions((actions) => actions.selections.setMobileSelections);

  useEffect(() => {
    if (!hasDiscoveryResult(deviceState)) {
      history.replace(previousStepPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasDiscoveryResult(deviceState)) {
    return null;
  }

  const { discoveryResult } = deviceState;

  const handleBack = () => {
    history.goBack();
  };

  const handleStartOver = () => {
    history.push("/register-device");
  };

  const handleFinish = async () => {
    // The wizard's login/registration step already obtained a real session
    // token — in-memory only, so it doesn't trigger the app's post-login
    // bootstrap mid-wizard (see existingUserFlowApi.ts's `login`). Now that
    // the wizard is done, promote it into the app's real session (both
    // branches) instead of discarding it.
    //
    // The new-user branch's registration token is scoped to a different app
    // (appId "coolAutomationControl") than the Commercial dashboard requires
    // — unlike the existing-user branch's login(), which already requests
    // "CoolCommercial". Re-authenticate with the credentials just created to
    // get a token the dashboard will actually accept.
    const personalDetails = (deviceState as Partial<NewUserFlowState>).personalDetails;
    let token = sdkXhr.getToken();

    if (personalDetails) {
      try {
        const { data } = await User.connect(personalDetails.email, personalDetails.password, "CoolCommercial");
        token = data?.token;
      } catch {
        token = undefined;
      }
    }

    if (token) {
      localStorage.setItem("token", token);
      sdkXhr.setToken(token);
      setLoggedIn(true);

      // The site/customer just created (or picked) during registration
      // should already be selected on the dashboard the user lands on,
      // instead of defaulting to none — true for both branches, since
      // `customerId`/`siteId` are part of the state shared between them
      // (see `DiscoveryFlowState`). This registration flow is reached from a
      // mobile-only entry point ("Register Now" on the login screen), but
      // the app is responsive, so write both the desktop selection
      // (`selections`) and the mobile one (`mobileSelections`) — they're
      // separate store slices, read by separate UI (SelectionsMenu vs.
      // TopFiltersMenu/MobileDashboard).
      const { customerId, siteId } = deviceState;
      if (customerId && siteId) {
        setSelections({ customerId, siteId });
        setMobileSelections({ customerId, siteId, unitId: "" });
      }
    }
    // The wizard's temporary token has now been promoted to a real session
    // (or discarded above) — either way it's no longer "pending".
    clearPublicRegistrationTokenFlag();
    history.push("/");
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Connect Device`}</span>
        <span />
      </div>

      <div className={parentStyles.discoveryContent}>
        <Typography variant="pageTitle">{t`Congratulations!`}</Typography>
        <Typography className={parentStyles.discoveryCaption}>
          {t`connected to device:`} {discoveryResult.deviceMac}
        </Typography>
        <img src={HVACImg} alt={t`HVAC device`} className={parentStyles.discoveryDevice} />
        {discoveryResult.hadError && (
          <div className={parentStyles.warningBanner}>
            {t`Due to a technical error, the process stopped midway. Please try again.`}
          </div>
        )}
        <div className={parentStyles.indoorUnitsRow}>
          <Typography variant="label">{t`Indoor Units`}</Typography>
          <TextField disabled fullWidth={false} value={`# ${discoveryResult.indoorUnitsCount}`} />
        </div>
        {discoveryResult.hadError ? (
          <Button variant="contained" className={parentStyles.primaryButton} onClick={handleStartOver}>
            {t`Start Over`}
          </Button>
        ) : (
          <Button variant="contained" className={parentStyles.primaryButton} onClick={handleFinish}>
            {t`Next`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CongratulationsStep;
